import type { QuaternionSignalModalityV1, SignalNativeSeriesV1, SignalWindowV1 } from "@rekonas/domain";

export type QuaternionWxyz = readonly [number, number, number, number];

export type OrientationSelection =
  | { state: "available"; sensor: QuaternionWxyz; model: QuaternionWxyz; sampleTime: number }
  | { state: "unavailable"; reason: "out_of_coverage" | "gap" | "invalid" | "mapping" };

const SQRT_HALF = Math.SQRT1_2;
export const RX_NEGATIVE_90: QuaternionWxyz = [SQRT_HALF, -SQRT_HALF, 0, 0];

export function normalizeQuaternion(value: QuaternionWxyz): QuaternionWxyz | null {
  if (value.some((component) => !Number.isFinite(component))) return null;
  const norm = Math.hypot(...value);
  if (norm < 1e-6 || norm < 0.9 || norm > 1.1) return null;
  return value.map((component) => component / norm) as unknown as QuaternionWxyz;
}

export function multiplyQuaternion(left: QuaternionWxyz, right: QuaternionWxyz): QuaternionWxyz {
  const [aw, ax, ay, az] = left;
  const [bw, bx, by, bz] = right;
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
}

export function slerpQuaternion(left: QuaternionWxyz, right: QuaternionWxyz, amount: number): QuaternionWxyz {
  let target = right;
  let dot = left.reduce((sum, component, index) => sum + component * target[index], 0);
  if (dot < 0) {
    target = target.map((component) => -component) as unknown as QuaternionWxyz;
    dot = -dot;
  }
  dot = Math.max(-1, Math.min(1, dot));
  if (dot > 0.9995) {
    const blended = left.map((component, index) => component + amount * (target[index] - component)) as unknown as QuaternionWxyz;
    return normalizeQuaternion(blended) || left;
  }
  const theta = Math.acos(dot);
  const sine = Math.sin(theta);
  const a = Math.sin((1 - amount) * theta) / sine;
  const b = Math.sin(amount * theta) / sine;
  return left.map((component, index) => a * component + b * target[index]) as unknown as QuaternionWxyz;
}

function sample(series: SignalNativeSeriesV1[], index: number): QuaternionWxyz | null {
  const raw = series.map((item) => item.values[index]);
  if (raw.some((value) => value == null || !Number.isFinite(value))) return null;
  return normalizeQuaternion(raw as unknown as QuaternionWxyz);
}

export function orientationAtTime(
  window: SignalWindowV1,
  modality: QuaternionSignalModalityV1,
  selectedTime: number,
  calibration: QuaternionWxyz = RX_NEGATIVE_90,
): OrientationSelection {
  const aliases = [modality.components.w, modality.components.x, modality.components.y, modality.components.z];
  const source = aliases.map((choices) => window.series.find((candidate) =>
    candidate.representation === "native" && candidate.modality_id === modality.id && choices.includes(candidate.channel_label),
  ));
  if (source.some((item) => !item || item.representation !== "native")) return { state: "unavailable", reason: "mapping" };
  const series = source as SignalNativeSeriesV1[];
  const start = series[0].start_seconds;
  const period = series[0].sample_period_seconds;
  if (series.some((item) => Math.abs(item.start_seconds - start) > 1e-9 || Math.abs(item.sample_period_seconds - period) > 1e-9)) {
    return { state: "unavailable", reason: "mapping" };
  }
  const last = start + (series[0].sample_count - 1) * period;
  if (selectedTime < start - 1e-9 || selectedTime > last + 1e-9) return { state: "unavailable", reason: "out_of_coverage" };
  const position = Math.max(0, Math.min(series[0].sample_count - 1, (selectedTime - start) / period));
  const lowerIndex = Math.floor(position + 1e-9);
  const upperIndex = Math.ceil(position - 1e-9);
  const lower = sample(series, lowerIndex);
  const upper = sample(series, upperIndex);
  if (!lower || !upper) return { state: "unavailable", reason: "gap" };
  const lowerTime = start + lowerIndex * period;
  const upperTime = start + upperIndex * period;
  const maximumGap = Math.min(modality.quaternion.max_gap_seconds, period * 2 + 1e-9);
  if (upperTime - lowerTime > maximumGap) return { state: "unavailable", reason: "gap" };
  const sensor = lowerIndex === upperIndex ? lower : slerpQuaternion(lower, upper, (selectedTime - lowerTime) / (upperTime - lowerTime));
  const model = normalizeQuaternion(multiplyQuaternion(calibration, sensor));
  if (!model) return { state: "unavailable", reason: "invalid" };
  return { state: "available", sensor, model, sampleTime: selectedTime };
}

export function quaternionToXyzw(value: QuaternionWxyz): readonly [number, number, number, number] {
  return [value[1], value[2], value[3], value[0]];
}

export function quaternionEulerDegrees(value: QuaternionWxyz): { pitch: number; roll: number; yaw: number } {
  const [w, x, y, z] = value;
  const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y));
  const pitch = Math.asin(Math.max(-1, Math.min(1, 2 * (w * y - z * x))));
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  const degrees = 180 / Math.PI;
  return { pitch: pitch * degrees, roll: roll * degrees, yaw: yaw * degrees };
}
